/**
 * Road network geometry for the district mission (ADR-022). The drivable
 * area is the UNION of road pieces — straight/polyline bands (alleys,
 * streets, junction stubs) and a roundabout ring — described by an exact
 * signed-distance field (negative inside). Everything the sim needs from
 * the curbs (collision clamp, free-distance ray marching, clearance, the
 * road-relative control frame) is a query on that field, so junctions and
 * curves need no special cases. Pure module: no DOM, no Babylon.
 *
 * Coordinates: x east, z north. Headings are radians from +z toward +x
 * (sin -> x, cos -> z), matching the rest of the sim.
 */

import { clamp, wrapAngle, type Vec2 } from "../core/vec";

export type RoadType = "alley" | "street" | "roundabout";

export interface BandPiece {
  kind: "band";
  id: string;
  roadType: "alley" | "street";
  /** Centerline polyline (world coords). Joins and caps are round. */
  pts: Vec2[];
  halfWidth: number;
  /** False for pieces that only shape the drivable area (ring mouth flares):
   * they never define the control frame, so "straight" keeps following the
   * ring instead of a join chord. Default true. */
  frame?: boolean;
  /** Traffic runs only in the direction `pts` is listed. Renderer paints
   * lane arrows; the map checker holds traffic to it. The sim itself does
   * not enforce it — agents follow the paths their map gives them. */
  oneWay?: boolean;
  /** Lanes per direction; markings only. Default 1. */
  lanes?: number;
}

export interface RingPiece {
  kind: "ring";
  id: string;
  roadType: "roundabout";
  cx: number;
  cz: number;
  /** Island radius (inner curb) and outer curb radius. */
  rIn: number;
  rOut: number;
  /** Circulation sense of the traffic: sign of d(psi)/dt where
   * psi = atan2(x - cx, z - cz). Counter-clockwise viewed from above
   * (right-hand traffic) is -1. */
  dir: 1 | -1;
}

export type RoadPiece = BandPiece | RingPiece;

export interface PieceFrame {
  index: number;
  piece: RoadPiece;
  /** Signed distance of the query point to this piece (negative inside). */
  sdf: number;
  /** Road direction (yaw) closest to the supplied heading hint. */
  tangent: number;
}

const GRAD_EPS = 0.01;
const MARCH_MIN_STEP = 0.05;
/** Penetration tolerated while grazing a curb (the wall clamp leaves bodies exactly touching). */
const GRAZE_TOL = 0.02;

/** Distance from p to segment ab. */
function distToSegment(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const vx = bx - ax;
  const vz = bz - az;
  const wx = px - ax;
  const wz = pz - az;
  const vv = vx * vx + vz * vz;
  const u = vv < 1e-12 ? 0 : clamp((wx * vx + wz * vz) / vv, 0, 1);
  return Math.hypot(px - (ax + vx * u), pz - (az + vz * u));
}

/** Nearest segment index + distance along a polyline. */
function nearestSegment(pts: Vec2[], px: number, pz: number): { index: number; dist: number } {
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const d = distToSegment(px, pz, a.x, a.z, b.x, b.z);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return { index: best, dist: bestD };
}

export function pieceSdf(piece: RoadPiece, x: number, z: number): number {
  if (piece.kind === "band") {
    return nearestSegment(piece.pts, x, z).dist - piece.halfWidth;
  }
  const r = Math.hypot(x - piece.cx, z - piece.cz);
  return Math.max(piece.rIn - r, r - piece.rOut);
}

/** A band's forward direction (its polyline order) at (x, z); rings: the
 * circulation tangent. */
export function pieceForward(piece: RoadPiece, x: number, z: number): number {
  if (piece.kind === "ring") {
    const psi = Math.atan2(x - piece.cx, z - piece.cz);
    return wrapAngle(psi + (piece.dir * Math.PI) / 2);
  }
  const { index } = nearestSegment(piece.pts, x, z);
  const a = piece.pts[index]!;
  const b = piece.pts[index + 1]!;
  return Math.atan2(b.x - a.x, b.z - a.z);
}

/** Road direction of a piece at (x, z), choosing the sign closest to `hint`
 * for two-way bands (rings are one-way). */
export function pieceTangent(piece: RoadPiece, x: number, z: number, hint: number): number {
  if (piece.kind === "ring") {
    const psi = Math.atan2(x - piece.cx, z - piece.cz);
    return wrapAngle(psi + (piece.dir * Math.PI) / 2);
  }
  const { index } = nearestSegment(piece.pts, x, z);
  const a = piece.pts[index]!;
  const b = piece.pts[index + 1]!;
  const yaw = Math.atan2(b.x - a.x, b.z - a.z);
  const back = wrapAngle(yaw + Math.PI);
  return Math.abs(wrapAngle(yaw - hint)) <= Math.abs(wrapAngle(back - hint)) ? yaw : back;
}

export class RoadNetwork {
  readonly pieces: readonly RoadPiece[];
  /** Axis-aligned bounds of all road geometry (renderer/sanity use). */
  readonly bounds: { minX: number; maxX: number; minZ: number; maxZ: number };

  constructor(pieces: RoadPiece[]) {
    if (pieces.length === 0) throw new Error("network needs >= 1 piece");
    this.pieces = pieces;
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const p of pieces) {
      if (p.kind === "band") {
        for (const q of p.pts) {
          minX = Math.min(minX, q.x - p.halfWidth);
          maxX = Math.max(maxX, q.x + p.halfWidth);
          minZ = Math.min(minZ, q.z - p.halfWidth);
          maxZ = Math.max(maxZ, q.z + p.halfWidth);
        }
      } else {
        minX = Math.min(minX, p.cx - p.rOut);
        maxX = Math.max(maxX, p.cx + p.rOut);
        minZ = Math.min(minZ, p.cz - p.rOut);
        maxZ = Math.max(maxZ, p.cz + p.rOut);
      }
    }
    this.bounds = { minX, maxX, minZ, maxZ };
  }

  /** Signed distance to the drivable area's boundary (negative = on the road). */
  sdf(x: number, z: number): number {
    let best = Infinity;
    for (const p of this.pieces) best = Math.min(best, pieceSdf(p, x, z));
    return best;
  }

  /** Clearance from a point to the nearest curb (positive = inside the road). */
  clearance(x: number, z: number): number {
    return -this.sdf(x, z);
  }

  /** Outward unit normal of the boundary nearest to (x, z) (finite difference). */
  gradient(x: number, z: number): Vec2 {
    const gx = this.sdf(x + GRAD_EPS, z) - this.sdf(x - GRAD_EPS, z);
    const gz = this.sdf(x, z + GRAD_EPS) - this.sdf(x, z - GRAD_EPS);
    const len = Math.hypot(gx, gz);
    return len < 1e-9 ? { x: 0, z: 0 } : { x: gx / len, z: gz / len };
  }

  /** Deepest piece among those allowed to define the control frame. */
  private deepestFramePiece(x: number, z: number): number {
    let best = -1;
    let bestD = Infinity;
    for (let i = 0; i < this.pieces.length; i++) {
      const p = this.pieces[i]!;
      if (p.kind === "band" && p.frame === false) continue;
      const d = pieceSdf(p, x, z);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best < 0 ? this.deepestPiece(x, z) : best;
  }

  /** Index of the piece containing (x, z) most deeply. */
  deepestPiece(x: number, z: number): number {
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < this.pieces.length; i++) {
      const d = pieceSdf(this.pieces[i]!, x, z);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best;
  }

  /**
   * Road frame at (x, z): among the pieces the point is within `inset` of
   * (sdf < inset; a small POSITIVE inset tolerates the curb pockets at
   * junction corners), the one whose direction best matches `headingHint`;
   * falls back to the deepest piece. `keep` (a previous index) wins unless
   * another piece is clearly better — hysteresis at junction overlaps.
   */
  frameAt(x: number, z: number, headingHint: number, keep: number | null, inset = 0.35): PieceFrame {
    let bestIdx = -1;
    let bestScore = Infinity;
    for (let i = 0; i < this.pieces.length; i++) {
      const p = this.pieces[i]!;
      if (p.kind === "band" && p.frame === false) continue;
      const d = pieceSdf(p, x, z);
      if (d > inset) continue;
      const tangent = pieceTangent(p, x, z, headingHint);
      let score = Math.abs(wrapAngle(tangent - headingHint));
      if (i === keep) score -= 0.5; // hysteresis: stay unless clearly better
      if (d > 0) score += 0.3; // inside beats "near"
      if (score < bestScore) {
        bestScore = score;
        bestIdx = i;
      }
    }
    if (bestIdx < 0) bestIdx = keep ?? this.deepestFramePiece(x, z);
    const piece = this.pieces[bestIdx]!;
    return {
      index: bestIdx,
      piece,
      sdf: pieceSdf(piece, x, z),
      tangent: pieceTangent(piece, x, z, headingHint),
    };
  }

  /**
   * Distance a circle of radius r can travel from (x, z) along (dx, dz)
   * before touching a curb (sphere tracing on the SDF; exact for this union
   * of exact distance fields). Up to maxDist.
   */
  freeAlong(x: number, z: number, dx: number, dz: number, r: number, maxDist: number): number {
    // A body already touching the curb is free to move ALONG or away from it
    // (the straight-wall model always allowed that); only a ray pointing into
    // the curb is blocked at zero.
    const m0 = -this.sdf(x, z) - r;
    if (m0 <= GRAZE_TOL) {
      const g = this.gradient(x, z);
      if (dx * g.x + dz * g.z > 0.05) return 0;
    }
    let d = MARCH_MIN_STEP;
    for (let i = 0; i < 400 && d < maxDist; i++) {
      const margin = -this.sdf(x + dx * d, z + dz * d) - r;
      if (margin <= -GRAZE_TOL) return Math.max(0, d - MARCH_MIN_STEP);
      d += Math.max(margin, MARCH_MIN_STEP);
    }
    return Math.min(d, maxDist);
  }

  /**
   * Project (x, z) so a circle of radius r sits inside the road (slides along
   * the curb; never fails). Returns the corrected point.
   */
  pushInside(x: number, z: number, r: number): Vec2 {
    let px = x;
    let pz = z;
    for (let i = 0; i < 3; i++) {
      const pen = this.sdf(px, pz) + r;
      if (pen <= 0) break;
      const g = this.gradient(px, pz);
      px -= g.x * pen;
      pz -= g.z * pen;
    }
    return { x: px, z: pz };
  }
}
